/* Dafodila Offers - ticket slider, details pop-up and Shiprocket "Shop now" */
(() => {
  if (customElements.get('dafodila-offers')) return;

  /* ---------- Page scroll lock (shared by every offer pop-up) ----------
     Locks only the element that actually scrolls the page (the window, or .page-wrapper
     when the theme scrolls inside it), so the sticky header keeps its place.
     The scrollbar's width is kept as padding so the page does not jump sideways. */
  const scrollLock = {
    count: 0,
    el: null,
    overflow: '',
    padding: '',

    findScroller() {
      const root = document.scrollingElement || document.documentElement;
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) {
        const oy = getComputedStyle(wrapper).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
      }
      return root;
    },

    lock() {
      this.count += 1;
      if (this.count > 1) return;
      const el = this.findScroller();
      const isRoot = el === document.scrollingElement || el === document.documentElement;
      const gap = isRoot ? window.innerWidth - document.documentElement.clientWidth : el.offsetWidth - el.clientWidth;
      this.el = el;
      this.overflow = el.style.overflow;
      this.padding = el.style.paddingRight;
      if (gap > 0) {
        const current = parseFloat(getComputedStyle(el).paddingRight) || 0;
        el.style.paddingRight = `${current + gap}px`;
      }
      el.style.overflow = 'hidden';
    },

    unlock() {
      if (this.count === 0) return;
      this.count -= 1;
      if (this.count > 0 || !this.el) return;
      this.el.style.overflow = this.overflow;
      this.el.style.paddingRight = this.padding;
      this.el = null;
    },
  };

  class DafodilaOffers extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.track = this.querySelector('.dofr__track');
      this.prev = this.querySelector('.dofr__arrow--prev');
      this.next = this.querySelector('.dofr__arrow--next');
      this.dots = this.querySelector('.dofr__dots');

      this.addEventListener('click', (event) => this.onClick(event));

      this.querySelectorAll('dialog.dofr__dialog').forEach((dialog) => {
        // Tap on the dark area closes the pop-up
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        // Unlock the page and return focus to the ticket that opened it
        dialog.addEventListener('close', () => {
          if (dialog._dofrLocked) {
            dialog._dofrLocked = false;
            scrollLock.unlock();
          }
          if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
          this.opener = null;
        });
      });

      if (this.track) {
        this.track.addEventListener('scroll', () => this.schedule(), { passive: true });
        if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
        if (this.next) this.next.addEventListener('click', () => this.page(1));
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => this.schedule());
          this.ro.observe(this.track);
        }
        this.buildDots();
        this.update();
      }
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.querySelectorAll('dialog.dofr__dialog').forEach((dialog) => {
        if (dialog._dofrLocked) {
          dialog._dofrLocked = false;
          scrollLock.unlock();
        }
      });
    }

    get items() {
      return Array.from(this.querySelectorAll('.dofr__item'));
    }

    onClick(event) {
      const opener = event.target.closest('[data-dofr-open]');
      if (opener) {
        this.openDialog(opener);
        return;
      }
      const closer = event.target.closest('[data-dofr-close]');
      if (closer) {
        const dialog = closer.closest('dialog');
        if (dialog) dialog.close();
        return;
      }
      const copy = event.target.closest('[data-dofr-copy]');
      if (copy) {
        this.copyCode(copy);
        return;
      }
      const buy = event.target.closest('[data-dofr-buy]');
      if (buy) this.buyNow(buy);
    }

    openDialog(opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (!dialog || dialog.open) return;
      this.opener = opener;
      if (!dialog._dofrLocked) {
        dialog._dofrLocked = true;
        scrollLock.lock();
      }
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    /* ---------- Shop now: hand over to the page's own Shiprocket button ---------- */

    // The Shiprocket "Buy now" button sits inside the product form, so it always uses the
    // size and quantity the shopper picked on the page.
    findShiprocketButton() {
      const selector = 'form[action*="/cart/add"] button.sr-headless-checkout';
      const scope = this.closest('.product-details, product-info, .product-information') || document;
      return scope.querySelector(selector) || document.querySelector(selector);
    }

    buyNow(button) {
      const dialog = button.closest('dialog');
      this.opener = null; // don't jump back to the ticket while checkout opens
      if (dialog) dialog.close();

      const shiprocket = this.findShiprocketButton();
      if (shiprocket) {
        button.disabled = true;
        shiprocket.click();
        setTimeout(() => {
          button.disabled = false;
        }, 2500);
        return;
      }

      // Fallback (Shiprocket not loaded yet): take the shopper to the buy buttons
      const scope = this.closest('.product-details') || document;
      const form = scope.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
      const target = form && (form.querySelector('.shiprocket-headless, [type="submit"]') || form);
      if (target) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const focusable = target.matches('button, [type="submit"]') ? target : target.querySelector('button');
        if (focusable) focusable.focus({ preventScroll: true });
      }
    }

    /* ---------- Coupon code ---------- */

    copyCode(button) {
      const code = button.getAttribute('data-dofr-copy') || '';
      const original = button.textContent;
      const done = () => {
        button.textContent = 'Copied';
        clearTimeout(button._t);
        button._t = setTimeout(() => {
          button.textContent = original;
        }, 1600);
      };
      const fallback = () => {
        const field = document.createElement('textarea');
        field.value = code;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        (button.closest('dialog') || document.body).appendChild(field);
        field.select();
        try {
          document.execCommand('copy');
        } catch (error) {
          /* ignore */
        }
        field.remove();
        done();
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done, fallback);
      } else {
        fallback();
      }
    }

    /* ---------- Slider ---------- */

    buildDots() {
      if (!this.dots) return;
      const count = this.items.length;
      this.dots.innerHTML = '';
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('span');
        dot.className = 'dofr__dot';
        this.dots.appendChild(dot);
      }
    }

    page(direction) {
      if (!this.track) return;
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: 'smooth' });
    }

    schedule() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    }

    update() {
      const track = this.track;
      if (!track) return;
      const max = track.scrollWidth - track.clientWidth;
      const overflow = max > 4;
      const left = track.scrollLeft;

      if (this.prev) this.prev.hidden = !overflow || left <= 4;
      if (this.next) this.next.hidden = !overflow || left >= max - 4;

      if (!this.dots) return;
      this.dots.hidden = !overflow;
      if (!overflow) return;

      const items = this.items;
      if (this.dots.children.length !== items.length) this.buildDots();
      let active = 0;
      if (left >= max - 4) {
        active = items.length - 1;
      } else {
        const start = track.getBoundingClientRect().left;
        let best = Infinity;
        items.forEach((item, i) => {
          const distance = Math.abs(item.getBoundingClientRect().left - start);
          if (distance < best) {
            best = distance;
            active = i;
          }
        });
      }
      Array.from(this.dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    }
  }

  customElements.define('dafodila-offers', DafodilaOffers);
})();
